import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Send, Loader2, CreditCard } from "lucide-react";

const getSessionId = (uid) => {
  const key = `pb_assist_session_${uid}`;
  let sid = localStorage.getItem(key);
  if (!sid) {
    sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, sid);
  }
  return sid;
};

export const FloatingAssistant = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [quota, setQuota] = useState(null);
  const endRef = useRef(null);
  const sid = user ? getSessionId(user.id) : null;

  useEffect(() => {
    if (open && !loaded && sid) {
      api.get(`/assistant/history?session_id=${sid}`)
        .then(({ data }) => setMsgs(data))
        .catch(() => {})
        .finally(() => setLoaded(true));
      api.get("/assistant/quota").then(({ data }) => setQuota(data)).catch(() => {});
    }
  }, [open, loaded, sid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy, open]);

  if (!user) return null;

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m.map((x) => ({ ...x, options: [] })), { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: text, session_id: sid, origin_url: window.location.origin });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, pay_url: data.pay_url, options: data.options || [] }]);
      if (data.quota_limit != null) setQuota((qt) => ({ ...(qt || {}), limit: data.quota_limit, remaining: data.quota_remaining }));
    } catch (e) {
      const detail = e.response?.status === 429 ? e.response?.data?.detail : null;
      if (detail) setQuota((qt) => ({ ...(qt || {}), remaining: 0 }));
      setMsgs((m) => [...m, { role: "assistant", text: detail || "Something went wrong — try again, or text us at 941-899-5300." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-44 left-4 sm:left-6 z-[65] w-[calc(100vw-2rem)] max-w-sm bg-pb-surface border border-pb-cyan shadow-2xl shadow-black/60 flex flex-col animate-fade-up"
          style={{ height: "min(520px, calc(100vh - 13rem))" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-pb-border bg-pb-elevated/60">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pb-cyan" />
              <div className="leading-none">
                <div className="font-display font-black uppercase text-sm">Bullpen AI</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Ask me anything about the site</div>
                {quota?.limit != null && (
                  <div className={`text-[9px] uppercase tracking-[0.2em] mt-0.5 ${quota.remaining > 0 ? "text-pb-cyan" : "text-amber-400"}`} data-testid="assistant-quota">
                    {quota.remaining > 0 ? `${quota.remaining} of ${quota.limit} question${quota.limit !== 1 ? "s" : ""} left today` : "Daily limit reached — upgrade for more"}
                  </div>
                )}
              </div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="assistant-messages">
            {msgs.length === 0 && !busy && (
              <div className="text-xs text-zinc-500 space-y-2 pt-2">
                <p className="text-zinc-400 font-semibold">Hey {user.name?.split(" ")[0]}! I can help with anything on the Bullpen:</p>
                {["What sessions can I book this week?", "Book me into the next open session", "When is my next tournament match?", "How do I raise my PSI?"].map((q) => (
                  <button key={q} onClick={() => { setInput(q); }}
                    className="block w-full text-left px-3 py-2 border border-pb-border/60 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-pb-red text-black font-medium" : "bg-pb-elevated border border-pb-border/60 text-zinc-200"}`}>
                  {m.text}
                  {m.pay_url && (
                    <a data-testid="assistant-pay-link" href={m.pay_url} target="_blank" rel="noreferrer"
                      className="mt-2 flex items-center justify-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-3 py-2 text-[11px] hover:bg-[#BEF264] transition-colors">
                      <CreditCard className="w-3.5 h-3.5" /> Pay by Card
                    </a>
                  )}
                </div>
                {m.role === "assistant" && i === msgs.length - 1 && !busy && (m.options || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 max-w-[85%]" data-testid="assistant-options">
                    {m.options.map((o) => (
                      <button key={o} data-testid={`assistant-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                        onClick={() => send(o)}
                        className="px-3 py-1.5 border border-pb-cyan/60 text-pb-cyan text-xs font-semibold hover:bg-pb-cyan hover:text-black transition-colors">
                        {o}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-pb-elevated border border-pb-border/60 px-3 py-2 text-sm text-zinc-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-pb-cyan" /> Thinking…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-pb-border flex gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about PSI, booking, tournaments…"
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
            <button data-testid="assistant-send" onClick={() => send()} disabled={busy || !input.trim()}
              className="w-10 flex items-center justify-center bg-pb-red text-black hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <button data-testid="assistant-fab" onClick={() => setOpen(!open)} title="Bullpen AI — ask me anything"
        className={`fixed bottom-24 left-4 sm:left-6 z-[65] w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-black/50 transition-all active:scale-90 ${
          open ? "bg-pb-elevated border border-pb-cyan text-pb-cyan" : "bg-pb-red text-black hover:scale-105"}`}>
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-6 h-6" />}
      </button>
    </>
  );
};

export default FloatingAssistant;
